/**
 * Facilitator tooling of the scripted usability sessions (W3-05; Appendix C H2; usability.ts):
 *
 *  - `UsabilityChip`: a status bar item, shown only while a session runs: "Task 2 of 5: Animate a line", a mark once
 *    the automatic check saw the goal reached, Done / Gave up (which start the next task) and Stop;
 *  - `UsabilityRoute` (`#/dev/usability`): the task sheet to read from (what to say, what to watch for, what counts
 *    as reached) and the last logged session with Save as JSON and Delete;
 *  - the palette commands `lane.usabilityStart` / `lane.usabilityStop`.
 */

import { useEffect, useReducer, useState } from 'react';
import type { AppServices, CommandDef } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { m } from './noteLane.messages';
import { ref } from './laneSession';
import {
  USABILITY_STORAGE_KEY,
  USABILITY_TASKS,
  createUsabilityLogger,
  loadUsabilityLog,
  setUsabilitySession,
  subscribeUsabilitySession,
  usabilitySession,
  type ParticipantProfile,
  type UsabilityLog,
} from './usability';
import s from './Usability.module.css';

function useSessionVersion(): void {
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    let offLog: (() => void) | null = null;
    const watch = (): void => {
      offLog?.();
      offLog = usabilitySession()?.subscribe(bump) ?? null;
      bump();
    };
    watch();
    const off = subscribeUsabilitySession(watch);
    return () => {
      off();
      offLog?.();
    };
  }, []);
}

/** Status bar item: the current task and the facilitator's buttons (nothing when no session runs). */
export function UsabilityChip(_props: { readonly services: AppServices }) {
  useSessionVersion();
  const t = useT();
  const session = usabilitySession();
  if (session === null || !session.active) return null;
  const i = session.taskIndex;
  const task = USABILITY_TASKS[i];
  const rec = session.log.tasks[session.log.tasks.length - 1];
  return (
    <div className={s.chip} role="group" aria-label={t(m.uxPageTitle)} data-usability="">
      <Icon name="ClipboardList" size={14} />
      <span className={s.chipText}>
        {task === undefined
          ? t(m.uxChipDone)
          : t(m.uxChip, { n: i + 1, count: USABILITY_TASKS.length, title: t(task.title) })}
      </span>
      {task !== undefined && rec?.reachedMs !== null && rec?.reachedMs !== undefined ? (
        <span className={s.reached}>
          <Icon name="Check" size={12} /> {t(m.uxReached)}
        </span>
      ) : null}
      {task !== undefined ? (
        <>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              session.endTask('done');
            }}
          >
            {t(m.uxDone)}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              session.endTask('gaveUp');
            }}
          >
            {t(m.uxGaveUp)}
          </Button>
        </>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        icon="Square"
        onClick={() => {
          setUsabilitySession(null);
        }}
      >
        {t(m.uxStop)}
      </Button>
    </div>
  );
}

function download(log: UsabilityLog): void {
  const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `usability-${log.participant}.json`;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

/** `#/dev/usability`: the task sheet and the last session's log. */
export function UsabilityRoute(_props: { readonly services: AppServices }) {
  const t = useT();
  const [log, setLog] = useState<UsabilityLog | null>(() => loadUsabilityLog());
  return (
    <main className={s.page}>
      <h1>{t(m.uxPageTitle)}</h1>
      <p>{t(m.uxPageIntro)}</p>
      <p className={s.muted}>{t(m.uxPageStart)}</p>
      <ol className={s.tasks}>
        {USABILITY_TASKS.map((task) => (
          <li key={task.id}>
            <h2>{t(task.title)}</h2>
            <dl>
              <dt>{t(m.uxSay)}</dt>
              <dd>{t(task.script)}</dd>
              <dt>{t(m.uxObserve)}</dt>
              <dd>{t(task.observe)}</dd>
              <dt>{t(m.uxSuccess)}</dt>
              <dd>{t(task.success)}</dd>
            </dl>
          </li>
        ))}
      </ol>
      <section className={s.log}>
        {log === null ? (
          <p>{t(m.uxNoLog)}</p>
        ) : (
          <>
            <p>
              {t(m.uxLastLog, {
                participant: log.participant,
                profile: t(log.profile === 'rpe' ? m.uxProfileRpe : m.uxProfileFirstTime),
                tasks: log.tasks.length,
                events: log.events.length,
              })}
            </p>
            <ul>
              {log.tasks.map((r) => {
                const task = USABILITY_TASKS.find((x) => x.id === r.id);
                const seconds = Math.round(((r.endMs ?? r.reachedMs ?? r.startMs) - r.startMs) / 1000);
                return (
                  <li key={`${r.id}-${String(r.startMs)}`}>
                    {t(m.uxTaskOutcome, {
                      title: task === undefined ? r.id : t(task.title),
                      outcome:
                        r.outcome === null ? t(m.uxOutcomeNone) : t(r.outcome === 'done' ? m.uxDone : m.uxGaveUp),
                      seconds,
                      commits: r.commits,
                      undos: r.undos,
                    })}
                  </li>
                );
              })}
            </ul>
            <div className={s.actions}>
              <Button
                icon="Download"
                onClick={() => {
                  download(log);
                }}
              >
                {t(m.uxSaveLog)}
              </Button>
              <Button
                variant="ghost"
                icon="Trash2"
                onClick={() => {
                  try {
                    localStorage.removeItem(USABILITY_STORAGE_KEY);
                  } catch {
                    // nothing stored
                  }
                  setLog(null);
                }}
              >
                {t(m.uxClearLog)}
              </Button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export const USABILITY_COMMANDS: readonly CommandDef[] = [
  {
    id: 'lane.usabilityStart',
    title: m.cmdUsabilityStart,
    category: 'Help',
    icon: 'ClipboardList',
    aliases: ['usability test', 'user test', 'task sheet'],
    level: 'guided',
    enabled: (ctx) => (ctx.store === null ? ref(m.reasonNoProject) : true),
    async run(ctx) {
      const profile = await ctx.ui.choose<ParticipantProfile>({
        title: ref(m.uxProfileTitle),
        options: [
          { value: 'firstTime', label: ref(m.uxProfileFirstTime) },
          { value: 'rpe', label: ref(m.uxProfileRpe) },
        ],
      });
      if (profile === null) return;
      const id = `P-${new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '')}`;
      const logger = createUsabilityLogger(ctx, id, profile);
      setUsabilitySession(logger);
      logger.startTask(0);
    },
  },
  {
    id: 'lane.usabilityStop',
    title: m.cmdUsabilityStop,
    category: 'Help',
    icon: 'Square',
    level: 'guided',
    enabled: () => (usabilitySession()?.active === true ? true : ref(m.uxNoLog)),
    run(ctx) {
      setUsabilitySession(null);
      ctx.ui.toast(ref(m.uxStopped), { kind: 'info', durationMs: 6000 });
    },
  },
];
